/* PTL → PTL Billing → Ad hoc Charges.
   Pre-billing (D9, D10): only shipments with an entry here have ad hoc charges. KAM agrees the charge with the client
   first and adds the client amount with the approval; Ops adds what the courier charges us. A charge is complete when
   both sides are in. Cards filter the list. Amounts exclude GST. */
import React, { useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { Alert, Box, Button, Card, Chip, IconButton, TextField, Tooltip, Typography } from '@mui/material'
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined'
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline'
import { useSnackbar } from 'notistack'
import PageTitle from '../../components/PageTitle'
import ProzoDataGrid from '../../common/ProzoDataGrid'
import { apiConstants, get, getDownload } from '../../common/constants'
import { kpiRowSx } from '../../common/sharedStyles'
import { saveBlob } from '../freight-profitability/freightPnlUtils'
import { BILLING_BASE, useAdhocListQuery, useDeleteAdhocMutation } from '../../redux/ptlBillingApiSlice'
import KpiTile from './components/KpiTile'
import MerchantPicker from './components/MerchantPicker'
import FilterSelect, { FilterSlot } from './components/FilterSelect'
import { FilterActions, FilterCardContent, FilterRow, ResetButton } from '../../common/FilterBar'
import AdhocChargeDialog from './components/AdhocChargeDialog'
import AdhocBulkDialog from './components/AdhocBulkDialog'
import { ADHOC_STATE, errText, fmtDate, inr, recentMonths } from './billingUtils'

const STATES = [
  ['ALL', 'All charges'], ['INCOMPLETE', 'Incomplete (KAM or Ops)'], ['WAITING_OPS', 'Waiting for Ops (courier cost)'],
  ['WAITING_KAM', 'Waiting for KAM (client amount)'], ['COMPLETE', 'Complete'],
]

export default function AdhocCharges() {
  const [params, setParams] = useSearchParams()
  const filter = {
    merchantOid: params.get('merchant') || undefined,
    state: params.get('state') || 'ALL',
    awb: params.get('awb') || undefined,
    cycleMonth: params.get('cycleMonth') || undefined,
    page: Number(params.get('page') || 1),
    size: Number(params.get('size') || 20),
  }
  const setF = (patch) => {
    const next = { merchant: filter.merchantOid, state: filter.state, awb: filter.awb, cycleMonth: filter.cycleMonth, page: 1, size: filter.size, ...patch }
    setParams(Object.fromEntries(Object.entries(next).filter(([, v]) => v !== undefined && v !== null && v !== '' && !(v === 'ALL'))))
  }
  const { data, isFetching, error } = useAdhocListQuery(filter)
  const [del] = useDeleteAdhocMutation()
  const { enqueueSnackbar } = useSnackbar()
  const [dialog, setDialog] = useState(null) // { mode, charge }
  const [bulkOpen, setBulkOpen] = useState(false)
  const [awbInput, setAwbInput] = useState(filter.awb || '')

  const downloadTemplate = async () => {
    try {
      const res = await getDownload(`${apiConstants.BASE_LOCAL}${BILLING_BASE}/adhoc/bulk/template`)
      saveBlob(res.data, 'PTL_Adhoc_Charges_Template.xlsx')
    } catch (e) {
      enqueueSnackbar('Could not download the template.', { variant: 'error' })
    }
  }
  const viewProof = async (row) => {
    try {
      const res = await get(`${BILLING_BASE}/adhoc/${row.id}/proof`)
      window.open(res.data.url, '_blank', 'noopener')
    } catch (e) {
      enqueueSnackbar(errText(e?.response, 'Could not open the approval.'), { variant: 'error' })
    }
  }
  const remove = async (row) => {
    if (!window.confirm(`Delete ${row.chargeTypeLabel} on ${row.awb}?${row.walletDebited > 0 ? ` ${inr(row.walletDebited)} is credited back to the wallet.` : ''}`)) return
    const res = await del({ id: row.id, version: row.version })
    if (res.error) enqueueSnackbar(errText(res.error), { variant: 'error' })
    else enqueueSnackbar('Ad hoc charge deleted', { variant: 'success' })
  }

  const columns = [
    { field: 'awb', headerName: 'AWB', flex: 1, minWidth: 130, renderCell: ({ row }) => <Box><b>{row.awb}</b><Typography sx={{ fontSize: 11, color: 'text.secondary' }}>{row.cycleMonth ? `Billing ${row.cycleMonth}` : 'Not prepared yet'}</Typography></Box> },
    { field: 'merchantName', headerName: 'Client', flex: 1, minWidth: 140 },
    { field: 'chargeTypeLabel', headerName: 'Charge type', flex: 1, minWidth: 140, renderCell: ({ row }) => <Box>{row.chargeTypeLabel}{row.otherDesc && <Typography sx={{ fontSize: 11, color: 'text.secondary' }}>{row.otherDesc}</Typography>}</Box> },
    { field: 'clientAmount', headerName: 'Client side (KAM)', minWidth: 140, align: 'right', headerAlign: 'right',
      renderCell: ({ row }) => (row.clientAmount != null ? <AmountLink row={row} value={row.clientAmount} onClick={() => setDialog({ mode: 'kam', charge: row })} />
        : <Button size='small' variant='outlined' disabled={row.locked} onClick={() => setDialog({ mode: 'kam', charge: row })} sx={{ textTransform: 'none', borderRadius: 5 }}>+ Add (KAM)</Button>) },
    { field: 'approval', headerName: 'Client approval', minWidth: 120, renderCell: ({ row }) => (row.clientAmount == null ? '—'
      : row.hasProof ? <Button size='small' startIcon={<VisibilityOutlinedIcon />} onClick={() => viewProof(row)} sx={{ textTransform: 'none' }}>View</Button>
        : <Chip size='small' label='NOT NEEDED' variant='outlined' />) },
    { field: 'courierAmount', headerName: 'Courier side (Ops)', minWidth: 140, align: 'right', headerAlign: 'right',
      renderCell: ({ row }) => (row.courierAmount != null ? <AmountLink row={row} value={row.courierAmount} onClick={() => setDialog({ mode: 'ops', charge: row })} />
        : <Button size='small' variant='outlined' disabled={row.locked} onClick={() => setDialog({ mode: 'ops', charge: row })} sx={{ textTransform: 'none', borderRadius: 5 }}>+ Add (Ops)</Button>) },
    { field: 'marginOnCharge', headerName: 'Margin on charge', minWidth: 120, align: 'right', headerAlign: 'right',
      renderCell: ({ row }) => (row.marginOnCharge == null ? '—' : <Typography sx={{ fontSize: 13, color: row.marginOnCharge < 0 ? 'error.main' : 'success.dark' }}>{inr(row.marginOnCharge)}</Typography>) },
    { field: 'state', headerName: 'Status', minWidth: 130, renderCell: ({ row }) => <Box>
      <Chip size='small' color={ADHOC_STATE[row.state]?.color} label={(ADHOC_STATE[row.state]?.label || row.state).toUpperCase()} />
      {row.locked && <Typography sx={{ fontSize: 11, color: 'text.secondary' }}>Locked ({row.lineStatus})</Typography>}</Box> },
    { field: 'remarks', headerName: 'Remarks', flex: 1.2, minWidth: 180, renderCell: ({ row }) => <Typography sx={{ fontSize: 12, whiteSpace: 'normal' }}>{row.remarks || '—'}</Typography> },
    { field: 'by', headerName: 'Added by', minWidth: 150, renderCell: ({ row }) => <Typography sx={{ fontSize: 12 }}>
      {[row.kamByName || row.kamBy, row.opsByName || row.opsBy].filter(Boolean).join(' / ') || row.createdBy}<br />{fmtDate(row.createdAt)}{row.source === 'BULK' ? ' · bulk' : ''}</Typography> },
    { field: 'actions', headerName: '', minWidth: 56, sortable: false, renderCell: ({ row }) => (row.locked ? null
      : <Tooltip title='Delete'><IconButton size='small' color='error' onClick={() => remove(row)}><DeleteOutlineIcon fontSize='small' /></IconButton></Tooltip>) },
  ]

  const incomplete = (data?.waitingOps || 0) + (data?.waitingKam || 0)
  return (
    <Box>
      <PageTitle>Ad hoc Charges</PageTitle>
      <Card elevation={0} sx={{ border: 1, borderColor: 'divider', p: 2, mb: 2, display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
        <Typography sx={{ fontSize: 13, color: 'text.secondary', flex: 1, minWidth: 280 }}>
          Only shipments with an entry here have ad hoc charges; every other shipment has none. KAM adds the client amount with the client's approval;
          Ops adds what the courier charges us. A charge is complete when both sides are in. Prepaid clients: exactly the client amount is debited from the wallet (no GST); an edit moves only the difference, a delete credits it back.
        </Typography>
        <Button variant='outlined' onClick={downloadTemplate} sx={{ textTransform: 'none' }}>Template</Button>
        <Button variant='contained' color='warning' onClick={() => setBulkOpen(true)} sx={{ textTransform: 'none' }}>Bulk upload</Button>
        <Button variant='contained' onClick={() => setDialog({ mode: 'add' })} sx={{ textTransform: 'none' }}>Add ad hoc charge</Button>
      </Card>

      {incomplete > 0 && (
        <Alert severity='warning' sx={{ mb: 2 }} action={<Button color='inherit' size='small' onClick={() => setF({ state: 'INCOMPLETE' })}>Show them</Button>}>
          <b>{incomplete} ad hoc {incomplete === 1 ? "charge" : "charges"} incomplete</b>: {data.waitingOps} waiting for the courier cost from Ops, {data.waitingKam} waiting for the client amount
          and approval from KAM. A charge without the client amount is not billed.
        </Alert>
      )}

      <Box sx={{ ...kpiRowSx, mb: 2 }}>
        <KpiTile label='Ad hoc charges' value={data?.charges ?? '—'} sub={data ? `on ${data.shipments} shipments` : ''} selected={filter.state === 'ALL'} onClick={() => setF({ state: 'ALL' })} />
        <KpiTile label='Waiting for Ops' accent='error' value={data?.waitingOps ?? '—'} sub='Courier cost not added yet' selected={filter.state === 'WAITING_OPS'} onClick={() => setF({ state: 'WAITING_OPS' })} />
        <KpiTile label='Waiting for KAM' accent='warning' value={data?.waitingKam ?? '—'} sub='Client amount + approval not added' selected={filter.state === 'WAITING_KAM'} onClick={() => setF({ state: 'WAITING_KAM' })} />
        <KpiTile label='Client side (KAM)' accent='success' value={inr(data?.clientTotal, 0)} sub={data ? `Courier side ${inr(data.courierTotal, 0)}` : ''} selected={filter.state === 'COMPLETE'} onClick={() => setF({ state: 'COMPLETE' })} />
      </Box>

      <Card elevation={0} sx={{ border: 1, borderColor: 'divider' }}>
        <FilterCardContent>
          <FilterRow>
            <FilterSlot label='Client' sx={{ flex: '2 1 240px' }}>
              <MerchantPicker value={filter.merchantOid || null} onChange={(m) => setF({ merchant: m })} sx={{ minWidth: 0 }} />
            </FilterSlot>
            <FilterSelect label='Show' value={filter.state} allLabel={null} onChange={(v) => setF({ state: v })} options={STATES.map(([v, l]) => ({ value: v, label: l }))}
              sx={{ flex: '2 1 220px' }} />
            <FilterSelect label='Service month' value={filter.cycleMonth || ''} allLabel='All months' onChange={(v) => setF({ cycleMonth: v || undefined })} options={recentMonths(12)} />
            <FilterSlot label='AWB' sx={{ flex: '2 1 220px' }}>
              <TextField size='small' fullWidth value={awbInput} onChange={(e) => setAwbInput(e.target.value)} inputProps={{ 'aria-label': 'AWB' }}
                onKeyDown={(e) => { if (e.key === 'Enter') setF({ awb: awbInput.trim() || undefined }) }} placeholder='Type an AWB and press Enter' />
            </FilterSlot>
            <FilterActions>
              <ResetButton onClick={() => { setAwbInput(''); setParams({}) }}>Reset</ResetButton>
            </FilterActions>
          </FilterRow>
        </FilterCardContent>
        {error && <Alert severity='error' sx={{ mx: 2, mb: 2 }}>{errText(error, 'Could not load the ad hoc charges.')}</Alert>}
        {!error && (
          <ProzoDataGrid
            columns={columns}
            rows={data?.rows || []}
            rowCount={data?.total || 0}
            paginationMode='server'
            page={filter.page - 1}
            setPage={(p) => setF({ page: p + 1 })}
            sizePerPage={filter.size}
            setSizePerPage={(s) => setF({ size: s, page: 1 })}
            loading={isFetching}
            getRowId={(r) => r.id}
          />
        )}
        <Typography sx={{ p: 2, fontSize: 12, color: 'text.secondary' }}>Click an amount to change that side. The same charge type twice on one AWB needs a confirmation. Charges of an approved or billed shipment are locked.</Typography>
      </Card>

      {dialog && <AdhocChargeDialog mode={dialog.mode} charge={dialog.charge} onClose={() => setDialog(null)} />}
      {bulkOpen && <AdhocBulkDialog onClose={() => setBulkOpen(false)} onTemplate={downloadTemplate} />}
    </Box>
  )
}

/** An amount that opens its side for a change (plain text when the shipment is locked). */
function AmountLink({ row, value, onClick }) {
  if (row.locked) return inr(value)
  return <Button size='small' onClick={onClick} sx={{ textTransform: 'none', fontVariantNumeric: 'tabular-nums', color: 'text.primary' }}>{inr(value)}</Button>
}
